import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon, CheckIcon, XMarkIcon, KeyIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import DocSmartButton from './DocSmartButton';
import CiActivityPanel from './common/CiActivityPanel';
import CiSelector from './CiSelector';
import FormCardsShell from './FormCardsShell';
import FormCardsHead from './FormCardsHead';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import '../styles/DetailView.css';
import DetailCrumb from './DetailCrumb';

// Una licencia sólo se puede asignar a estos tipos (el backend rechaza el resto).
const TIPOS_ASIGNABLES = ['rack_element', 'virt_vm', 'workstation', 'service', 'database_instance'];
const ETIQUETAS_TIPO = {
  rack_element: 'Servidor físico',
  virt_vm: 'Máquina virtual',
  workstation: 'Estación de trabajo',
  service: 'Servicio',
  database_instance: 'Instancia de base de datos',
};

const EMPTY = {
  product: '',
  version: '',
  license_type: 'per_core',
  license_key: '',
  quantity: 1,
  vendor: '',
  contract_ref: '',
  purchase_date: '',
  expiry_date: '',
  status: 'active',
  assigned_ci_type: '',
  assigned_ci_id: '',
  software_installation_id: '',
  notes: '',
};

function expiryHint(expiryDate, warningDays) {
  if (!expiryDate) return 'Sin caducidad (licencia perpetua)';
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const fin = new Date(`${expiryDate}T00:00:00`);
  if (Number.isNaN(fin.getTime())) return '';
  const dias = Math.round((fin - hoy) / 86400000);
  if (dias < 0) return `Caducó hace ${Math.abs(dias)} día(s)`;
  if (dias === 0) return 'Caduca hoy';
  if (dias <= warningDays) return `Caduca en ${dias} día(s) — entra en el aviso del panel`;
  return `Caduca en ${dias} día(s)`;
}

export default function LicenseForm({ license, companyId, meta = {}, onClose, onSaved, initialMode, variant = 'classic', navItems = [], navIndex = -1, onNavigate }) {
  const isCards = variant === 'cards';
  /* La ficha se abre EN LECTURA (clic en la fila del listado); 'create' y 'edit'
     editan. Sin `initialMode` edita, como siempre. */
  const [editing, setEditing] = useState(initialMode ? initialMode !== 'view' : true);
  const isEdit = Boolean(license?.id);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showKey, setShowKey] = useState(false);

  const warningDays = meta.expiry_warning_days ?? 60;
  const typeLabels = meta.license_type_labels || {};
  const statusLabels = meta.license_status_labels || {};
  // Sólo se ofrecen los tipos a los que el backend permite asignar una licencia.
  const opcionesTipoCi = (meta.assignable_ci_types || TIPOS_ASIGNABLES).map((v) => ({
    value: v,
    label: ETIQUETAS_TIPO[v] || v,
  }));

  useEffect(() => {
    if (license) {
      setForm({
        product: license.product || '',
        version: license.version || '',
        license_type: license.license_type || 'per_core',
        license_key: license.license_key || '',
        quantity: license.quantity ?? 1,
        vendor: license.vendor || '',
        contract_ref: license.contract_ref || '',
        purchase_date: license.purchase_date || '',
        expiry_date: license.expiry_date || '',
        status: license.status || 'active',
        assigned_ci_type: license.assigned_ci_type || '',
        assigned_ci_id: license.assigned_ci_id || '',
        software_installation_id: license.software_installation_id || '',
        notes: license.notes || '',
      });
    } else {
      setForm(EMPTY);
    }
    setError('');
    setIsDirty(false);
  }, [license]);

  /** Cambia un campo y marca la ficha como modificada (para el dock). */
  const sf = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    sf(name, value);
    setError('');
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (!form.product.trim()) { setError('Indica el producto'); return false; }
    setSaving(true); setError('');
    try {
      const payload = {
        ...form,
        quantity: form.quantity === '' ? 1 : Number(form.quantity),
        assigned_ci_id: form.assigned_ci_id ? Number(form.assigned_ci_id) : null,
        software_installation_id: form.software_installation_id
          ? Number(form.software_installation_id)
          : null,
      };
      let creado = null;
      if (isEdit) await api.updateLicense(license.id, payload, companyId);
      else        creado = registroGuardado(await api.createLicense(payload, companyId));
      // Alta: el listado adopta el registro creado y la ficha sigue abierta ya
      // con id, así se encienden las zonas que necesitan estar guardado.
      onSaved?.(creado);
      setIsDirty(false);
      if (isCards) setEditing(false);
      return true;
    } catch (err) {
      setError(err.message || 'No se pudo guardar la licencia');
      return false;
    } finally { setSaving(false); }
  };

  // ── Variante tarjetas ────────────────────────────────────────────────────
  const ESTADO_COLOR = { active: '#16a34a', in_renewal: '#f59e0b', expired: '#dc2626', cancelled: '#94a3b8' };
  const diasCaducidad = (() => {
    if (!form.expiry_date) return null;
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    const fin = new Date(`${form.expiry_date}T00:00:00`);
    if (Number.isNaN(fin.getTime())) return null;
    return Math.round((fin - hoy) / 86400000);
  })();
  /** Cancelar edición: recarga lo guardado y sale de edición **sin cerrar** la ficha. */
  const handleCancelEdit = () => {
    if (license) {
      setForm({
        product: license.product || '',
        version: license.version || '',
        license_type: license.license_type || 'per_core',
        license_key: license.license_key || '',
        quantity: license.quantity ?? 1,
        vendor: license.vendor || '',
        contract_ref: license.contract_ref || '',
        purchase_date: license.purchase_date || '',
        expiry_date: license.expiry_date || '',
        status: license.status || 'active',
        assigned_ci_type: license.assigned_ci_type || '',
        assigned_ci_id: license.assigned_ci_id || '',
        software_installation_id: license.software_installation_id || '',
        notes: license.notes || '',
      });
    } else {
      setForm(EMPTY);
    }
    setIsDirty(false);
    setError('');
    setEditing(false);
  };
  const cardsHeader = (
    <FormCardsHead
      icon={<KeyIcon style={{ width: 20, height: 20 }} />}
      title={form.product}
      onTitleChange={(v) => { setForm((prev) => ({ ...prev, product: v })); setIsDirty(true); }}
      placeholder="Producto licenciado…"
      editing={editing}
      saving={saving}
      onSave={handleSave}
      onCancel={handleCancelEdit}
      onEdit={() => setEditing(true)}
      crumb={[form.version && `v${form.version}`, form.vendor].filter(Boolean).join(' › ')}
      badges={[
        { text: statusLabels[form.status] || form.status, color: ESTADO_COLOR[form.status] || '#94a3b8' },
        { text: typeLabels[form.license_type] || form.license_type },
      ]}
      chips={[
        { value: form.quantity || 1, label: 'cantidad' },
        { value: form.expiry_date || 'Perpetua', label: 'caducidad' },
        { value: form.contract_ref || '—', label: 'contrato' },
      ]}
    />
  );

  return (
    <div className="dv-overlay">
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={onClose}>
          <ArrowLeftIcon />
          Licencias
        </button>
        <DetailCrumb label={`${form.product || 'Nueva licencia'}${form.version ? ` · ${form.version}` : ''}`} />
        <FormPager
          items={navItems}
          index={navIndex}
          label="Licencia"
          disabled={saving}
          onChange={(destino) => {
            // Sin seguimiento fino de cambios: si se está editando, se avisa.
            if (!editing || window.confirm('Hay una edición abierta. ¿Descartar los cambios sin guardar y pasar a la siguiente licencia?')) {
              onNavigate?.(destino);
            }
          }}
        />
        <div className="dv-topbar-spacer" />
        {error && <span style={{ color: '#dc2626', fontSize: '0.85rem' }}>⚠ {error}</span>}
        <div className="dv-topbar-actions">
          {isCards ? (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
              <XMarkIcon />
              Cerrar
            </button>
          ) : !editing ? (
            <>
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon />
                Cerrar
              </button>
              <button className="dv-btn dv-btn-primary" type="button" onClick={() => setEditing(true)}>
                <CheckIcon />
                Editar
              </button>
            </>
          ) : (
            <>
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose} disabled={saving}>
                <XMarkIcon />
                Descartar
              </button>
              <button className="dv-btn dv-btn-primary" type="button" onClick={handleSave} disabled={saving}>
                {saving ? <span className="dv-spinner" /> : <CheckIcon />}
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="dv-smartbar">
      {isCards ? null : (
      <>
      <button type="button" className="dv-smart-btn">
          <div className="dv-smart-btn-icon"><KeyIcon /></div>
          <div className="dv-smart-btn-value">{form.license_type ? (typeLabels[form.license_type] || form.license_type) : '—'}</div>
          <div className="dv-smart-btn-label">Tipo</div>
        </button>
        <button type="button" className="dv-smart-btn">
          <div className="dv-smart-btn-icon">🔢</div>
          <div className="dv-smart-btn-value">{form.quantity || '—'}</div>
          <div className="dv-smart-btn-label">Cantidad</div>
        </button>
        <button type="button" className="dv-smart-btn">
          <div className="dv-smart-btn-icon">📅</div>
          <div className="dv-smart-btn-value">{form.expiry_date || 'Perpetua'}</div>
          <div className="dv-smart-btn-label">Caducidad</div>
        </button>
        <button type="button" className="dv-smart-btn">
          <div className="dv-smart-btn-icon">🚦</div>
          <div className="dv-smart-btn-value">{statusLabels[form.status] || form.status}</div>
          <div className="dv-smart-btn-label">Estado</div>
        </button>
        {license?.id && (
          <DocSmartButton elementType="license" elementId={license.id} elementName={form.name} companyId={companyId} />
        )}
      </>
      )}
      </div>

      {/* ── Body ── (en lectura se apaga de golpe con el fieldset) ── */}
      <div className="dv-body">
        {isCards && (
          <fieldset className="dv-fieldset sf2-main">
            <form className="dv-main sf2-main" onSubmit={handleSave}>
              <FormCardsShell isCards header={cardsHeader}>
                <fieldset className="dv-fieldset" disabled={!editing}>
                  <div className="dv-tab-pane">
                    <div className="sf2-hl" style={{ marginBottom: 12 }}>
                      <div className="sf2-kpi"><span>Cantidad</span><b>{form.quantity || 1}</b><small>{typeLabels[form.license_type] || form.license_type}</small></div>
                      <div className="sf2-kpi"><span>Caducidad</span><b style={{ fontSize: '0.95rem' }}>{form.expiry_date || 'Perpetua'}</b>{diasCaducidad != null && <small>{diasCaducidad < 0 ? `caducó hace ${Math.abs(diasCaducidad)} d` : `${diasCaducidad} d`}</small>}</div>
                      <div className="sf2-kpi"><span>Estado</span><b style={{ fontSize: '0.95rem' }}>{statusLabels[form.status] || form.status}</b></div>
                      <div className="sf2-kpi"><span>Aviso previo</span><b>{warningDays} d</b></div>
                    </div>

                    <div className="sf2-grid sf2-grid--2">
                      <div className="sf2-card">
                        <h3>Producto licenciado{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                        <div className="sf2-fields" style={{ '--n': 2 }}>
                          <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                            <label>Producto *</label>
                            {editing
                              ? <input value={form.product} onChange={e => sf('product', e.target.value)} placeholder="SQL Server 2019 Standard…" />
                              : <div className="v">{form.product || '—'}</div>}
                          </div>
                          <div className="sf2-field">
                            <label>Versión</label>
                            {editing
                              ? <input value={form.version} onChange={e => sf('version', e.target.value)} placeholder="15.0" />
                              : <div className="v">{form.version || '—'}</div>}
                          </div>
                          <div className="sf2-field">
                            <label>Tipo de licencia</label>
                            {editing ? (
                              <select value={form.license_type} onChange={e => sf('license_type', e.target.value)}>
                                {(meta.license_types || ['per_core', 'per_user', 'per_device', 'cal', 'subscription', 'oem', 'open_source', 'other']).map((t) => (
                                  <option key={t} value={t}>{typeLabels[t] || t}</option>
                                ))}
                              </select>
                            ) : <div className="v">{typeLabels[form.license_type] || form.license_type || '—'}</div>}
                          </div>
                          <div className="sf2-field">
                            <label>Cantidad</label>
                            {editing
                              ? <input type="number" min="1" value={form.quantity} onChange={e => sf('quantity', e.target.value)} />
                              : <div className="v">{form.quantity || 1}</div>}
                          </div>
                          <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                            <label>Clave / número de serie</label>
                            {editing ? (
                              <div style={{ display: 'flex', gap: 6 }}>
                                <input
                                  type={showKey ? 'text' : 'password'}
                                  value={form.license_key}
                                  onChange={e => sf('license_key', e.target.value)}
                                  placeholder="XXXXX-XXXXX-XXXXX-XXXXX-XXXXX"
                                  style={{ fontFamily: 'var(--font-mono)', flex: 1 }}
                                  autoComplete="off"
                                />
                                <button type="button" className="dv-btn dv-btn-secondary" onClick={() => setShowKey(v => !v)} title={showKey ? 'Ocultar la clave' : 'Mostrar la clave'}>
                                  {showKey ? '🙈' : '👁'}
                                </button>
                              </div>
                            ) : <div className="v">{form.license_key ? '••••••••' : '—'}</div>}
                          </div>
                        </div>
                      </div>

                      <div className="sf2-card">
                        <h3>Compra y vigencia{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                        <div className="sf2-fields" style={{ '--n': 2 }}>
                          <div className="sf2-field">
                            <label>Proveedor</label>
                            {editing
                              ? <input value={form.vendor} onChange={e => sf('vendor', e.target.value)} placeholder="Microsoft, Ingram…" />
                              : <div className="v">{form.vendor || '—'}</div>}
                          </div>
                          <div className="sf2-field">
                            <label>Contrato / pedido</label>
                            {editing
                              ? <input value={form.contract_ref} onChange={e => sf('contract_ref', e.target.value)} placeholder="CONT-2026-014" />
                              : <div className="v">{form.contract_ref || '—'}</div>}
                          </div>
                          <div className="sf2-field">
                            <label>Estado</label>
                            {editing ? (
                              <select value={form.status} onChange={e => sf('status', e.target.value)}>
                                {(meta.license_statuses || ['active', 'in_renewal', 'expired', 'cancelled']).map((s) => (
                                  <option key={s} value={s}>{statusLabels[s] || s}</option>
                                ))}
                              </select>
                            ) : <div className="v">{statusLabels[form.status] || form.status || '—'}</div>}
                          </div>
                          <div className="sf2-field">
                            <label>Fecha de compra</label>
                            {editing
                              ? <input type="date" value={form.purchase_date} onChange={e => sf('purchase_date', e.target.value)} />
                              : <div className="v">{form.purchase_date || '—'}</div>}
                          </div>
                          <div className="sf2-field">
                            <label>Caducidad</label>
                            {editing
                              ? <input type="date" value={form.expiry_date} onChange={e => sf('expiry_date', e.target.value)} />
                              : <div className="v">{form.expiry_date || 'Perpetua'}</div>}
                          </div>
                          <div className="sf2-field" style={{ alignSelf: 'end' }}>
                            <span className="sf2-hint">{expiryHint(form.expiry_date, warningDays)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="sf2-card">
                        <h3>Dónde está instalada{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                        {editing ? (
                          <CiSelector
                            ciType={form.assigned_ci_type}
                            ciId={form.assigned_ci_id}
                            companyId={companyId}
                            typeOptions={opcionesTipoCi}
                            onSelect={(ci) => { setForm((prev) => ({ ...prev, assigned_ci_type: ci.ci_type, assigned_ci_id: ci.ci_id })); setIsDirty(true); }}
                            onClear={() => { setForm((prev) => ({ ...prev, assigned_ci_type: '', assigned_ci_id: '' })); setIsDirty(true); }}
                          />
                        ) : (
                          <div className="sf2-field">
                            <div className="v">{license?.assigned_ci_name || '—'}</div>
                          </div>
                        )}
                      </div>

                      <div className="sf2-card">
                        <h3>Notas{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                        <div className="sf2-fields" style={{ '--n': 1 }}>
                          <div className="sf2-field">
                            {editing ? (
                              <textarea
                                className="dv-input"
                                rows={5}
                                value={form.notes}
                                onChange={e => sf('notes', e.target.value)}
                                placeholder="Notas de la licencia…"
                              />
                            ) : <div className="v">{form.notes || '—'}</div>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </fieldset>
                <FormDirtyDock
                  show={isCards && isDirty}
                  saving={saving}
                  onDiscard={handleCancelEdit}
                  onSave={() => handleSave()}
                />
              </FormCardsShell>
            </form>
          </fieldset>
        )}
        {!isCards && (
        <fieldset className="dv-fieldset" disabled={!editing}>
        <form className="dv-main" onSubmit={handleSave}>
          <div className="dv-tab-pane">
            <div className="dv-section">
              <div className="dv-section-title">Producto licenciado</div>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Producto *</label>
                  <input
                    name="product"
                    value={form.product}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="SQL Server 2019 Standard, Windows Server 2022…"
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Versión</label>
                  <input name="version" value={form.version} onChange={handleChange} className="form-control" placeholder="15.0" />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Tipo de licencia</label>
                  <select name="license_type" value={form.license_type} onChange={handleChange} className="form-select">
                    {(meta.license_types || ['per_core', 'per_user', 'per_device', 'cal', 'subscription', 'oem', 'open_source', 'other']).map((t) => (
                      <option key={t} value={t}>{typeLabels[t] || t}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Cantidad</label>
                  <input
                    name="quantity"
                    type="number"
                    min="1"
                    value={form.quantity}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="1"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Número de serie / clave de producto</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      name="license_key"
                      type={showKey ? 'text' : 'password'}
                      value={form.license_key}
                      onChange={handleChange}
                      className="form-control"
                      style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                      placeholder="XXXXX-XXXXX-XXXXX-XXXXX-XXXXX"
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      className="dv-btn dv-btn-secondary"
                      onClick={() => setShowKey((v) => !v)}
                      title={showKey ? 'Ocultar la clave' : 'Mostrar la clave'}
                    >
                      {showKey ? '🙈' : '👁'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="dv-section">
              <div className="dv-section-title">Compra y vigencia</div>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Proveedor</label>
                  <input name="vendor" value={form.vendor} onChange={handleChange} className="form-control" placeholder="Microsoft, Ingram…" />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Contrato / pedido</label>
                  <input name="contract_ref" value={form.contract_ref} onChange={handleChange} className="form-control" placeholder="CONT-2026-014" />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Estado</label>
                  <select name="status" value={form.status} onChange={handleChange} className="form-select">
                    {(meta.license_statuses || ['active', 'in_renewal', 'expired', 'cancelled']).map((s) => (
                      <option key={s} value={s}>{statusLabels[s] || s}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Fecha de compra</label>
                  <input name="purchase_date" type="date" value={form.purchase_date} onChange={handleChange} className="form-control" />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Caducidad (fin de SA / suscripción)</label>
                  <input name="expiry_date" type="date" value={form.expiry_date} onChange={handleChange} className="form-control" />
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '0.25rem' }}>
                    {expiryHint(form.expiry_date, warningDays)}
                  </div>
                </div>
              </div>
            </div>

            <div className="dv-section">
              <div className="dv-section-title">Dónde está instalada</div>
              <div className="row g-3">
                <div className="col-12">
                  <CiSelector
                    ciType={form.assigned_ci_type}
                    ciId={form.assigned_ci_id}
                    companyId={companyId}
                    typeOptions={opcionesTipoCi}
                    onSelect={(ci) => setForm((prev) => ({ ...prev, assigned_ci_type: ci.ci_type, assigned_ci_id: ci.ci_id }))}
                    onClear={() => setForm((prev) => ({ ...prev, assigned_ci_type: '', assigned_ci_id: '' }))}
                  />
                </div>
              </div>
            </div>
          </div>
        </form>

          {/* Lo que estaba en la barra lateral pasa al cuerpo: la columna
              derecha es para la actividad y los comentarios. */}
          <div className="dv-section">
            <div className="dv-section-title">Resumen</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.85rem' }}>
              <div><span style={{ color: '#64748b' }}>Producto:</span> <strong>{form.product || '—'}</strong></div>
              <div><span style={{ color: '#64748b' }}>Tipo:</span> <strong>{typeLabels[form.license_type] || form.license_type}</strong></div>
              <div><span style={{ color: '#64748b' }}>Cantidad:</span> <strong>{form.quantity || 1}</strong></div>
              {form.contract_ref && <div><span style={{ color: '#64748b' }}>Contrato:</span> {form.contract_ref}</div>}
              <div>
                <span style={{ color: '#64748b' }}>Caducidad:</span>{' '}
                <strong>{form.expiry_date || 'perpetua'}</strong>
              </div>
              {isEdit && license?.assigned_ci_name && (
                <div><span style={{ color: '#64748b' }}>Instalada en:</span> {license.assigned_ci_name}</div>
              )}
            </div>
          </div>
          <div className="dv-section">
            <div className="dv-section-title">¿Para qué sirve esto?</div>
            <p style={{ fontSize: '0.8rem', color: '#64748b', margin: 0 }}>
              Aquí se controla el número de serie del software que instalamos. Las licencias
              con caducidad entran en el aviso del panel {warningDays} días antes, para poder
              renovarlas a tiempo.
            </p>
          </div>
        </fieldset>
        )}
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="license"
          ciId={license?.id}
          tipoLegible="la licencia"
        />
      </div>
    </div>
  );
}
